import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Empty, Modal } from 'antd';
import type { FC } from 'react';

import type { Picture } from 'app/typings';

import { PictureComponent } from '../../PictureComponent/PictureComponent';
import { useImagePreview } from '../hooks/useImagePreview';

import './PictureList.scss';
import { PictureListItem } from './PictureListItem/PictureListItem';

type PictureListProps = {
  pictures: Picture[];
  uploadingItems: Record<string, number>;
  remove?: (uid: string) => void;
  edit?: (picture: Picture) => void;
  customButton?: React.ReactNode;
  previewable?: boolean;
  draggable?: boolean;
  fromUploader?: boolean;
  disabled?: boolean;
  onChange?: (pictures: Picture[]) => void;
};

export const PictureList: FC<PictureListProps> = ({
  pictures,
  uploadingItems,
  remove,
  edit,
  customButton = null,
  previewable = false,
  draggable = false,
  fromUploader = false,
  disabled = false,
  onChange,
}) => {
  const {
    previewVisible,
    previewImage,
    previewTitle,
    showPreview,
    closePreview,
  } = useImagePreview();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = pictures.findIndex(
        (pic) => `${pic.pictureId}-${pic.id}` === active.id
      );
      const newIndex = pictures.findIndex(
        (pic) => `${pic.pictureId}-${pic.id}` === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const newPictures = arrayMove(pictures, oldIndex, newIndex);

        onChange?.(newPictures);
      }
    }
  };

  const pictureIds = pictures.map((pic) => `${pic.pictureId}-${pic.id}`);

  return (
    <div className="picture-list">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={pictureIds} strategy={rectSortingStrategy}>
          {pictures.map((pic) => {
            const itemId = `${pic.pictureId}-${pic.id}`;

            return (
              <PictureListItem
                key={itemId}
                id={itemId}
                item={pic}
                draggable={draggable && !disabled}
                onPreview={previewable ? () => showPreview(pic) : undefined}
                onRemove={remove ? () => remove(itemId) : undefined}
                onEdit={edit && !disabled ? () => edit(pic) : undefined}
              />
            );
          })}
        </SortableContext>
      </DndContext>

      {uploadingItems &&
        Object.keys(uploadingItems).map((key) => (
          <div className="picture-list__uploading-item" key={key}>
            <span>Uploading ...</span>
          </div>
        ))}

      {!disabled && customButton && (
        <div className="picture-list__new-item">{customButton}</div>
      )}

      {!pictures?.length && !customButton && !fromUploader && !disabled && (
        <div className="picture-list__empty-list">
          <Empty />
        </div>
      )}

      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={closePreview}
        wrapClassName="picture-list-modal"
      >
        <div className="thumbnail">
          <PictureComponent pictureId={previewImage} alt="alt" width={472} />
        </div>
      </Modal>
    </div>
  );
};
